import React, { useMemo, useState } from "react";
import { Badge, Button, Card, Row, Col, Form, Table } from "react-bootstrap";
import {
  FaFileExcel,
  FaFilePdf,
  FaFileInvoiceDollar,
  FaSearch,
  FaFilter,
  FaHistory,
  FaClock,
  FaBuilding,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const toDateSafe = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    const d = new Date(`${v}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  if (/^\d{2}\/\d{2}\/\d{4}$/.test(v)) {
    const [dd, mm, yyyy] = v.split("/");
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const isPastDue = (dueDate, baseDate = new Date()) => {
  if (!dueDate) return false;
  const hoy = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate());
  return dueDate.getTime() < hoy.getTime();
};

const diffDays = (fromDate, toDate = new Date()) => {
  if (!fromDate || !toDate) return 0;
  const ms = toDate.getTime() - fromDate.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

const formatearMonto = (valor) =>
  Number(valor || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getStatusBadge = (diasVencidos) => {
  if (diasVencidos === 0) return { bg: "#10b981", label: "Al día" };
  if (diasVencidos < 15) return { bg: "#f59e0b", label: "Vencida" };
  if (diasVencidos < 30) return { bg: "#f97316", label: "Crítica" };
  return { bg: "#ef4444", label: "Urgente" };
};

const getCalificacionPago = (promediodias) => {
  if (promediodias <= 15) return { bg: "#10b981", label: "Excelente (<15d)" };
  if (promediodias <= 30) return { bg: "#0284c7", label: "Normal (15-30d)" };
  if (promediodias <= 60) return { bg: "#f59e0b", label: "Moderado (30-60d)" };
  return { bg: "#ef4444", label: "Lento (>60d)" };
};

const ReporteDiasCalleCobros = ({
  comprobantesPendientesCobro = [],
  movimientos = [],
  todosComprobantes = [],
  rucEmpresa = "",
}) => {
  const [vistaActiva, setVistaActiva] = useState("pendiente"); // 'pendiente' | 'historico'
  const [busqueda, setBusqueda] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todos");

  // 1. ANÁLISIS DE FACTURAS PENDIENTES DE COBRO (CARTERA ACTIVA)
  const reporteDiasCalleCobros = useMemo(() => {
    const hoy = new Date();
    const rows = (comprobantesPendientesCobro || [])
      .filter((c) => {
        const estadoPago = String(c?.estado_pago || "PENDIENTE").toUpperCase();
        return estadoPago !== "PAGADO";
      })
      .map((c) => {
        const fechaEmision = String(c.fecha_emision || "");
        const fechaVencimiento = String(c.fecha_vencimiento || c.fecha_vcto || c.fecha_emision || "");
        const dEmision = toDateSafe(fechaEmision);
        const dVenc = toDateSafe(fechaVencimiento);

        const fechaInicioMes = dEmision ? new Date(dEmision.getFullYear(), dEmision.getMonth(), 1) : hoy;
        const diasDesdeInicioMes = diffDays(fechaInicioMes, hoy);
        const diasEnCalleEmision = diffDays(dEmision, hoy);
        const diasVencidos = isPastDue(dVenc, hoy) ? diffDays(dVenc, hoy) : 0;

        return {
          id: c.id,
          tipo: c.tipo || "FACTURA",
          serie: c.serie || "",
          numero: c.numero || "",
          cliente: c.cliente_razon_social || c.cliente_ruc || "-",
          fechaEmision,
          fechaVencimiento,
          diasDesdeInicioMes,
          diasEnCalleEmision,
          diasVencidos,
          moneda: c.moneda || "PEN",
          total: Number(c.total || 0),
        };
      });

    return rows.sort((a, b) => b.diasVencidos - a.diasVencidos || b.diasDesdeInicioMes - a.diasDesdeInicioMes);
  }, [comprobantesPendientesCobro]);

  // 2. HISTÓRICO DE PROMEDIO DE DÍAS DE PAGO POR CLIENTE
  const historicoPagoClientes = useMemo(() => {
    // Mapa de comprobantes por serie-numero e ID para cruce exacto
    const compMapBySerieNum = {};
    const compMapById = {};
    (todosComprobantes || []).forEach((c) => {
      if (c.id) compMapById[c.id] = c;
      const s = String(c.serie || "").toUpperCase();
      const n = String(c.numero || "").toUpperCase();
      if (s && n) {
        compMapBySerieNum[`${s}-${n}`] = c;
        compMapBySerieNum[`${String(c.tipo || "").toUpperCase()}_${s}-${n}`] = c;
      }
    });

    const clienteMap = {};

    // A. Procesar cobros de movimientos
    const cobros = (movimientos || []).filter((m) => {
      const tipo = String(m?.tipo_movimiento || m?.tipo || "").toUpperCase();
      const flujo = String(m?.flujo || "").toUpperCase();
      return tipo === "COBRO" || flujo === "INGRESO";
    });

    cobros.forEach((m) => {
      let matchedComp = null;
      if (Array.isArray(m.documentos_relacionados)) {
        for (const docRel of m.documentos_relacionados) {
          if (docRel.id && compMapById[docRel.id]) {
            matchedComp = compMapById[docRel.id];
            break;
          }
          const s = String(docRel.serie || "").toUpperCase();
          const n = String(docRel.numero || "").toUpperCase();
          if (s && n && compMapBySerieNum[`${s}-${n}`]) {
            matchedComp = compMapBySerieNum[`${s}-${n}`];
            break;
          }
        }
      }
      if (!matchedComp && m.documentoRelacionado) {
        const s = String(m.documentoRelacionado.serie || "").toUpperCase();
        const n = String(m.documentoRelacionado.numero || "").toUpperCase();
        if (s && n && compMapBySerieNum[`${s}-${n}`]) {
          matchedComp = compMapBySerieNum[`${s}-${n}`];
        }
      }

      // Intentar extraer el nombre del cliente
      let cliente = String(
        m?.tercero ||
        m?.tercero_nombre ||
        m?.cliente ||
        m?.cliente_razon_social ||
        m?.razon_social ||
        m?.metadata?.tercero ||
        m?.metadata?.cliente ||
        matchedComp?.cliente_razon_social ||
        matchedComp?.cliente ||
        ""
      ).trim().toUpperCase();

      if (!cliente || cliente === "CLIENTE NO ESPECIFICADO" || cliente === "-") {
        if (matchedComp) {
          cliente = String(matchedComp.cliente_razon_social || matchedComp.cliente || matchedComp.tercero_nombre || "").trim().toUpperCase();
        }
      }

      if (!cliente || cliente === "CLIENTE NO ESPECIFICADO" || cliente === "-") {
        cliente = "CLIENTES VARIOS";
      }

      // Fechas para cálculo de días
      const fCobro = toDateSafe(m?.fecha || m?.created_at);
      let fEmision = toDateSafe(m?.fecha_emision || m?.fecha_comprobante);
      if (!fEmision && matchedComp) {
        fEmision = toDateSafe(matchedComp.fecha_emision || matchedComp.fecha);
      }

      let diasDemora = 0;
      if (fEmision && fCobro) {
        diasDemora = diffDays(fEmision, fCobro);
      }

      const monto = Number(m?.monto_total || m?.monto || 0);

      if (!clienteMap[cliente]) {
        clienteMap[cliente] = {
          cliente,
          totalCobradas: 0,
          sumaDias: 0,
          totalMonto: 0,
          minDias: diasDemora,
          maxDias: diasDemora,
        };
      }

      clienteMap[cliente].totalCobradas += 1;
      clienteMap[cliente].sumaDias += diasDemora;
      clienteMap[cliente].totalMonto += monto;
      if (diasDemora < clienteMap[cliente].minDias) clienteMap[cliente].minDias = diasDemora;
      if (diasDemora > clienteMap[cliente].maxDias) clienteMap[cliente].maxDias = diasDemora;
    });

    // B. Complementar con facturas de venta pagadas directamente en comprobantes (si aplican)
    (todosComprobantes || []).forEach((c) => {
      const estadoPago = String(c?.estado_pago || "").toUpperCase();
      if (estadoPago !== "PAGADO") return;

      const cliente = String(c?.cliente_razon_social || c?.cliente || c?.tercero_nombre || "").trim().toUpperCase();
      if (!cliente) return;

      const fEmision = toDateSafe(c.fecha_emision || c.fecha);
      const fCobro = toDateSafe(c.fecha_pago || c.fecha_cobro || c.updated_at);
      const monto = Number(c.total || 0);
      const diasDemora = (fEmision && fCobro) ? diffDays(fEmision, fCobro) : 0;

      // Si el cliente no existía en movimientos, agregarlo desde comprobantes
      if (!clienteMap[cliente]) {
        clienteMap[cliente] = {
          cliente,
          totalCobradas: 1,
          sumaDias: diasDemora,
          totalMonto: monto,
          minDias: diasDemora,
          maxDias: diasDemora,
        };
      }
    });

    const result = Object.values(clienteMap).map((item) => {
      const promedio = item.totalCobradas > 0 ? Math.round(item.sumaDias / item.totalCobradas) : 0;
      return {
        ...item,
        promedioDias: promedio,
      };
    });

    return result.sort((a, b) => b.promedioDias - a.promedioDias);
  }, [movimientos, todosComprobantes]);

  // FILTRADO PENDIENTES
  const datosPendientesFiltrados = useMemo(() => {
    let filtered = reporteDiasCalleCobros;

    if (busqueda.trim()) {
      const term = busqueda.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.cliente.toLowerCase().includes(term) ||
          `${r.tipo} ${r.serie}-${r.numero}`.toLowerCase().includes(term)
      );
    }

    if (filtroEstado === "vencidas") {
      filtered = filtered.filter((r) => r.diasVencidos > 0);
    } else if (filtroEstado === "aldia") {
      filtered = filtered.filter((r) => r.diasVencidos === 0);
    } else if (filtroEstado === "criticas") {
      filtered = filtered.filter((r) => r.diasVencidos >= 30);
    }

    return filtered;
  }, [reporteDiasCalleCobros, busqueda, filtroEstado]);

  // FILTRADO HISTÓRICO
  const datosHistoricosFiltrados = useMemo(() => {
    if (!busqueda.trim()) return historicoPagoClientes;
    const term = busqueda.toLowerCase();
    return historicoPagoClientes.filter((r) => r.cliente.toLowerCase().includes(term));
  }, [historicoPagoClientes, busqueda]);

  const totalGeneralPendiente = useMemo(
    () => datosPendientesFiltrados.reduce((acc, row) => acc + Number(row.total || 0), 0),
    [datosPendientesFiltrados]
  );

  const dsoPromedioMes = useMemo(() => {
    if (datosPendientesFiltrados.length === 0) return 0;
    const suma = datosPendientesFiltrados.reduce((acc, r) => acc + r.diasDesdeInicioMes, 0);
    return Math.round(suma / datosPendientesFiltrados.length);
  }, [datosPendientesFiltrados]);

  const promedioHistoricoGeneral = useMemo(() => {
    if (historicoPagoClientes.length === 0) return 0;
    const suma = historicoPagoClientes.reduce((acc, r) => acc + r.promedioDias, 0);
    return Math.round(suma / historicoPagoClientes.length);
  }, [historicoPagoClientes]);

  const exportarExcel = () => {
    if (vistaActiva === "pendiente") {
      const rows = datosPendientesFiltrados.map((r) => ({
        Comprobante: `${r.tipo || ""} ${r.serie || ""}-${r.numero || ""}`.trim(),
        Cliente: r.cliente,
        Estado: getStatusBadge(r.diasVencidos).label,
        Emisión: r.fechaEmision || "-",
        Vencimiento: r.fechaVencimiento || "-",
        "Días desde 1° Mes": r.diasDesdeInicioMes,
        "Días Vencidos": r.diasVencidos,
        Moneda: r.moneda,
        Total: Number(r.total || 0),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Dias_en_la_Calle");
      XLSX.writeFile(wb, `Dias_en_la_Calle_${rucEmpresa || "empresa"}.xlsx`);
    } else {
      const rows = datosHistoricosFiltrados.map((r) => ({
        Cliente: r.cliente,
        "Cobros Registrados": r.totalCobradas,
        "Promedio Días en Pagar": `${r.promedioDias} días`,
        "Rango Días": `${r.minDias} - ${r.maxDias} días`,
        Calificación: getCalificacionPago(r.promedioDias).label,
        "Total Cobrado (S/)": Number(r.totalMonto || 0),
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Promedio_Pago_Clientes");
      XLSX.writeFile(wb, `Promedio_Pago_Clientes_${rucEmpresa || "empresa"}.xlsx`);
    }
  };

  const exportarPdf = () => {
    const doc = new jsPDF("l", "mm", "a4");
    doc.setFontSize(14);
    if (vistaActiva === "pendiente") {
      doc.text("Reporte de Días en la Calle (DSO Cartera Activa)", 14, 14);
      doc.setFontSize(9);
      doc.text(`Empresa: ${rucEmpresa || "-"}`, 14, 20);
      doc.text(`Generado: ${new Date().toLocaleString("es-PE")}`, 14, 25);

      autoTable(doc, {
        startY: 30,
        head: [["Estado", "Comprobante", "Cliente", "Emisión", "Vencimiento", "Días 1° Mes", "Días Vencidos", "Total"]],
        body: datosPendientesFiltrados.map((r) => [
          getStatusBadge(r.diasVencidos).label,
          `${r.tipo || ""} ${r.serie || ""}-${r.numero || ""}`.trim(),
          r.cliente,
          r.fechaEmision || "-",
          r.fechaVencimiento || "-",
          `${r.diasDesdeInicioMes} d`,
          `${r.diasVencidos} d`,
          `${r.moneda} ${formatearMonto(r.total)}`,
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [15, 118, 110] },
      });

      doc.save(`Dias_Calle_${rucEmpresa || "empresa"}.pdf`);
    } else {
      doc.text("Histórico de Promedio de Días de Pago por Cliente", 14, 14);
      doc.setFontSize(9);
      doc.text(`Empresa: ${rucEmpresa || "-"}`, 14, 20);
      doc.text(`Generado: ${new Date().toLocaleString("es-PE")}`, 14, 25);

      autoTable(doc, {
        startY: 30,
        head: [["Cliente", "Cobros", "Promedio Días en Pagar", "Rango Días", "Calificación", "Total Cobrado"]],
        body: datosHistoricosFiltrados.map((r) => [
          r.cliente,
          r.totalCobradas,
          `${r.promedioDias} días`,
          `${r.minDias} - ${r.maxDias} d`,
          getCalificacionPago(r.promedioDias).label,
          `S/ ${formatearMonto(r.totalMonto)}`,
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [15, 118, 110] },
      });

      doc.save(`Promedio_Pago_Clientes_${rucEmpresa || "empresa"}.pdf`);
    }
  };

  return (
    <Card className="border-0 shadow-lg mt-3" style={{ borderRadius: "20px", overflow: "hidden" }}>
      {/* Header con Selectores de Vista */}
      <Card.Header
        className="border-0 py-4 px-4"
        style={{
          background: "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
          borderBottom: "none",
        }}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="text-white">
            <h5 className="fw-bold mb-1 d-flex align-items-center gap-2">
              <FaFileInvoiceDollar size={20} />
              Días en la Calle & Promedio de Pago por Cliente
            </h5>
            <small className="opacity-75">
              Análisis de cartera activa y comportamiento histórico de cobranza por cliente
            </small>
          </div>

          <div className="d-flex gap-2 align-items-center flex-wrap">
            {/* Pestañas de Vista */}
            <div
              className="p-1 rounded-pill d-flex gap-1"
              style={{ background: "rgba(0,0,0,0.2)", backdropFilter: "blur(10px)" }}
            >
              <Button
                size="sm"
                className="rounded-pill px-3 py-1 border-0"
                style={{
                  background: vistaActiva === "pendiente" ? "#ffffff" : "transparent",
                  color: vistaActiva === "pendiente" ? "#0f766e" : "#ffffff",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  transition: "all 0.2s ease",
                }}
                onClick={() => setVistaActiva("pendiente")}
              >
                <FaClock className="me-1" /> Cartera Pendiente ({reporteDiasCalleCobros.length})
              </Button>
              <Button
                size="sm"
                className="rounded-pill px-3 py-1 border-0"
                style={{
                  background: vistaActiva === "historico" ? "#ffffff" : "transparent",
                  color: vistaActiva === "historico" ? "#0f766e" : "#ffffff",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  transition: "all 0.2s ease",
                }}
                onClick={() => setVistaActiva("historico")}
              >
                <FaHistory className="me-1" /> Promedio de Pago Cliente ({historicoPagoClientes.length})
              </Button>
            </div>

            {/* Exportar */}
            <Button
              size="sm"
              className="rounded-pill px-3 d-flex align-items-center gap-2"
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.3)",
                color: "white",
              }}
              onClick={exportarPdf}
            >
              <FaFilePdf /> PDF
            </Button>
            <Button
              size="sm"
              className="rounded-pill px-3 d-flex align-items-center gap-2"
              style={{
                background: "#ffffff",
                border: "none",
                color: "#0f766e",
                fontWeight: 600,
              }}
              onClick={exportarExcel}
            >
              <FaFileExcel /> Excel
            </Button>
          </div>
        </div>
      </Card.Header>

      <Card.Body className="p-4" style={{ background: "linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)" }}>
        {/* VISTA 1: CARTERA PENDIENTES EN CALLE */}
        {vistaActiva === "pendiente" && (
          <>
            {/* KPI Summary Cards */}
            <Row className="mb-4 g-3">
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px", background: "#f0fdfa" }}>
                  <small className="text-muted fw-semibold">DSO Promedio del Periodo</small>
                  <h4 className="fw-bold mb-0" style={{ color: "#0f766e" }}>{dsoPromedioMes} días</h4>
                  <small className="text-muted" style={{ fontSize: "0.75rem" }}>Calculado desde el 1° del mes de emisión</small>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px", background: "#fffbeb" }}>
                  <small className="text-muted fw-semibold">Facturas Vencidas</small>
                  <h4 className="fw-bold mb-0 text-warning">
                    {reporteDiasCalleCobros.filter((r) => r.diasVencidos > 0).length} de {reporteDiasCalleCobros.length}
                  </h4>
                  <small className="text-muted" style={{ fontSize: "0.75rem" }}>Facturas pendientes con días de mora</small>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px", background: "#ffffff", border: "1px solid #e2e8f0" }}>
                  <small className="text-muted fw-semibold">Monto Total Cartera Activa</small>
                  <h4 className="fw-bold mb-0 text-dark">S/ {formatearMonto(totalGeneralPendiente)}</h4>
                  <small className="text-muted" style={{ fontSize: "0.75rem" }}>Saldo pendiente de cobro comercial</small>
                </Card>
              </Col>
            </Row>

            {/* Filtros */}
            <Row className="mb-4 g-3">
              <Col md={6}>
                <div className="position-relative">
                  <FaSearch
                    className="position-absolute text-muted"
                    style={{ left: "15px", top: "50%", transform: "translateY(-50%)" }}
                  />
                  <Form.Control
                    type="text"
                    placeholder="Buscar por cliente o comprobante..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="ps-5 border-0 shadow-sm"
                    style={{
                      borderRadius: "12px",
                      padding: "12px 15px 12px 45px",
                      background: "#f1f5f9",
                    }}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex gap-2 flex-wrap justify-content-md-end">
                  {[
                    { key: "todos", label: "Todos", color: "#64748b" },
                    { key: "vencidas", label: "Facturas Vencidas", color: "#f59e0b" },
                    { key: "criticas", label: "Críticas (+30d)", color: "#ef4444" },
                    { key: "aldia", label: "Al día", color: "#10b981" },
                  ].map(({ key, label, color }) => (
                    <Button
                      key={key}
                      size="sm"
                      className="rounded-pill px-3"
                      style={{
                        background: filtroEstado === key ? color : "transparent",
                        color: filtroEstado === key ? "white" : color,
                        border: `2px solid ${color}`,
                        fontWeight: 600,
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => setFiltroEstado(key)}
                    >
                      <FaFilter className="me-1" size={10} /> {label}
                    </Button>
                  ))}
                </div>
              </Col>
            </Row>

            {/* Tabla Cartera Activa */}
            {datosPendientesFiltrados.length === 0 ? (
              <div className="text-center py-5">
                <div
                  className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                  style={{
                    width: "80px",
                    height: "80px",
                    background: "linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 100%)",
                  }}
                >
                  <FaFileInvoiceDollar size={30} className="text-muted" />
                </div>
                <h6 className="text-muted">No hay facturas pendientes de cobro</h6>
                <small className="text-muted">Todas las facturas han sido cobradas. Puede revisar la pestaña "Promedio de Pago Cliente"</small>
              </div>
            ) : (
              <div
                className="table-responsive"
                style={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                  maxHeight: "550px",
                }}
              >
                <Table hover className="mb-0 align-middle" style={{ fontSize: "0.9rem" }}>
                  <thead style={{ background: "#0f766e", position: "sticky", top: 0, zIndex: 3 }}>
                    <tr>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 8px", textAlign: "center", width: "50px" }}>#</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px" }}>Estado</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px" }}>Comprobante</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px" }}>Cliente</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px" }}>Emisión</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px" }}>Vencimiento</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px", textAlign: "center" }}>Días 1° Mes</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px", textAlign: "center" }}>Días Vencidos</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px", textAlign: "right" }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datosPendientesFiltrados.slice(0, 100).map((r, idx) => {
                      const status = getStatusBadge(r.diasVencidos);
                      return (
                        <tr
                          key={`dias-calle-${r.id}`}
                          style={{
                            background: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                            transition: "all 0.2s ease",
                          }}
                          onMouseOver={(e) => (e.currentTarget.style.background = "#f0fdfa")}
                          onMouseOut={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : "#f8fafc")}
                        >
                          <td className="py-3 px-2 text-center">
                            <span className="fw-semibold text-muted">{idx + 1}</span>
                          </td>
                          <td className="py-3 px-3">
                            <Badge
                              style={{
                                background: status.bg,
                                color: "#ffffff",
                                fontWeight: 600,
                                padding: "6px 12px",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                              }}
                            >
                              {status.label}
                            </Badge>
                          </td>
                          <td className="py-3 px-3">
                            <span className="fw-semibold" style={{ color: "#0f766e" }}>
                              {r.tipo} {r.serie}-{r.numero}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <span className="fw-semibold text-truncate d-block" style={{ maxWidth: "250px" }}>
                              {r.cliente}
                            </span>
                          </td>
                          <td className="py-3 px-3">
                            <span className="text-muted">{r.fechaEmision || "-"}</span>
                          </td>
                          <td className="py-3 px-3">
                            <span className={r.diasVencidos > 0 ? "text-danger fw-semibold" : "text-muted"}>
                              {r.fechaVencimiento || "-"}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span
                              className="d-inline-block px-3 py-1 rounded-pill"
                              style={{ background: "#f1f5f9", fontWeight: 600, color: "#475569" }}
                            >
                              {r.diasDesdeInicioMes} d
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            {r.diasVencidos > 0 ? (
                              <span
                                className="d-inline-block px-3 py-1 rounded-pill"
                                style={{
                                  background: r.diasVencidos >= 30 ? "#fef2f2" : "#fffbeb",
                                  fontWeight: 700,
                                  color: r.diasVencidos >= 30 ? "#dc2626" : "#f59e0b",
                                }}
                              >
                                {r.diasVencidos} d
                              </span>
                            ) : (
                              <span
                                className="d-inline-block px-3 py-1 rounded-pill"
                                style={{ background: "#f0fdf4", fontWeight: 600, color: "#16a34a" }}
                              >
                                0 d
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-end">
                            <span className="fw-bold" style={{ color: "#0f766e" }}>
                              {r.moneda} {formatearMonto(r.total)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot
                    style={{
                      background: "#f0fdfa",
                      position: "sticky",
                      bottom: 0,
                      zIndex: 3,
                      borderTop: "3px solid #0f766e",
                      boxShadow: "0 -4px 10px rgba(0,0,0,0.08)",
                    }}
                  >
                    <tr>
                      <td colSpan={6} style={{ background: "#f0fdfa", color: "#0f766e", padding: "14px 16px" }}>
                        <span className="fw-bold" style={{ fontSize: "0.95rem" }}>
                          Total: {datosPendientesFiltrados.length} factura{datosPendientesFiltrados.length !== 1 ? "s" : ""} pendiente{datosPendientesFiltrados.length !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td colSpan={2} style={{ background: "#f0fdfa", color: "#0f766e", padding: "14px 16px", textAlign: "center" }}>
                        <span className="fw-semibold small">
                          Vencidas: {datosPendientesFiltrados.filter((r) => r.diasVencidos > 0).length}
                        </span>
                      </td>
                      <td style={{ background: "#f0fdfa", color: "#0f766e", padding: "14px 16px", textAlign: "right" }}>
                        <span className="fw-bold" style={{ fontSize: "1.15rem", color: "#0f766e" }}>
                          S/ {formatearMonto(totalGeneralPendiente)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}
          </>
        )}

        {/* VISTA 2: HISTÓRICO PROMEDIO DE DÍAS EN PAGAR POR CLIENTE */}
        {vistaActiva === "historico" && (
          <>
            {/* KPI Cards Histórico */}
            <Row className="mb-4 g-3">
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px", background: "#f0fdfa" }}>
                  <small className="text-muted fw-semibold">Promedio de Pago Cartera General</small>
                  <h4 className="fw-bold mb-0" style={{ color: "#0f766e" }}>{promedioHistoricoGeneral} días</h4>
                  <small className="text-muted" style={{ fontSize: "0.75rem" }}>Tiempo promedio de cobro acumulado</small>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px", background: "#f0f9ff" }}>
                  <small className="text-muted fw-semibold">Total Clientes Analizados</small>
                  <h4 className="fw-bold mb-0" style={{ color: "#0284c7" }}>{historicoPagoClientes.length} empresas</h4>
                  <small className="text-muted" style={{ fontSize: "0.75rem" }}>Con historial de cobros registrados</small>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="border-0 shadow-sm text-center py-3" style={{ borderRadius: "12px", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                  <small className="text-muted fw-semibold">Total Recaudado Histórico</small>
                  <h4 className="fw-bold mb-0 text-dark">
                    S/ {formatearMonto(historicoPagoClientes.reduce((a, b) => a + b.totalMonto, 0))}
                  </h4>
                  <small className="text-muted" style={{ fontSize: "0.75rem" }}>Suma total de cobros efectuados</small>
                </Card>
              </Col>
            </Row>

            {/* Búsqueda cliente */}
            <Row className="mb-4">
              <Col md={6}>
                <div className="position-relative">
                  <FaSearch
                    className="position-absolute text-muted"
                    style={{ left: "15px", top: "50%", transform: "translateY(-50%)" }}
                  />
                  <Form.Control
                    type="text"
                    placeholder="Buscar por cliente o empresa..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="ps-5 border-0 shadow-sm"
                    style={{
                      borderRadius: "12px",
                      padding: "12px 15px 12px 45px",
                      background: "#f1f5f9",
                    }}
                  />
                </div>
              </Col>
            </Row>

            {/* Tabla Histórica */}
            {datosHistoricosFiltrados.length === 0 ? (
              <div className="text-center py-5">
                <FaBuilding size={40} className="text-muted mb-3" />
                <h6 className="text-muted">No hay datos históricos de pagos registrados</h6>
                <small className="text-muted">Los cobros registrados en Tesorería aparecerán analizados en esta sección</small>
              </div>
            ) : (
              <div
                className="table-responsive"
                style={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
                  maxHeight: "550px",
                }}
              >
                <Table hover className="mb-0 align-middle" style={{ fontSize: "0.9rem" }}>
                  <thead style={{ background: "#0f766e", position: "sticky", top: 0, zIndex: 3 }}>
                    <tr>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 8px", textAlign: "center", width: "50px" }}>#</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px" }}>Cliente / Empresa</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px", textAlign: "center" }}>Cobros Realizados</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px", textAlign: "center" }}>Promedio Días en Pagar</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px", textAlign: "center" }}>Rango Días</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px", textAlign: "center" }}>Calificación</th>
                      <th style={{ background: "#0f766e", color: "#ffffff", fontWeight: 600, padding: "12px 16px", textAlign: "right" }}>Total Cobrado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datosHistoricosFiltrados.map((r, idx) => {
                      const cal = getCalificacionPago(r.promedioDias);
                      return (
                        <tr
                          key={`hist-cliente-${r.cliente}-${idx}`}
                          style={{
                            background: idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                            transition: "all 0.2s ease",
                          }}
                          onMouseOver={(e) => (e.currentTarget.style.background = "#f0fdfa")}
                          onMouseOut={(e) => (e.currentTarget.style.background = idx % 2 === 0 ? "#ffffff" : "#f8fafc")}
                        >
                          <td className="py-3 px-2 text-center">
                            <span className="fw-semibold text-muted">{idx + 1}</span>
                          </td>
                          <td className="py-3 px-3">
                            <span className="fw-bold text-dark">{r.cliente}</span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span className="fw-semibold px-3 py-1 rounded-pill" style={{ background: "#f1f5f9", color: "#475569" }}>
                              {r.totalCobradas} cobro{r.totalCobradas !== 1 ? "s" : ""}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center">
                            <span
                              className="fw-bold px-3 py-1 rounded-pill"
                              style={{
                                background: r.promedioDias <= 15 ? "#f0fdf4" : r.promedioDias <= 30 ? "#f0f9ff" : "#fffbeb",
                                color: r.promedioDias <= 15 ? "#16a34a" : r.promedioDias <= 30 ? "#0284c7" : "#d97706",
                                fontSize: "0.95rem",
                              }}
                            >
                              ⏱️ {r.promedioDias} días
                            </span>
                          </td>
                          <td className="py-3 px-3 text-center text-muted">
                            {r.minDias} - {r.maxDias} d
                          </td>
                          <td className="py-3 px-3 text-center">
                            <Badge
                              style={{
                                background: cal.bg,
                                color: "#ffffff",
                                fontWeight: 600,
                                padding: "6px 12px",
                                borderRadius: "20px",
                                fontSize: "0.75rem",
                              }}
                            >
                              {cal.label}
                            </Badge>
                          </td>
                          <td className="py-3 px-3 text-end">
                            <span className="fw-bold" style={{ color: "#0f766e" }}>
                              S/ {formatearMonto(r.totalMonto)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot
                    style={{
                      background: "#f0fdfa",
                      position: "sticky",
                      bottom: 0,
                      zIndex: 3,
                      borderTop: "3px solid #0f766e",
                      boxShadow: "0 -4px 10px rgba(0,0,0,0.08)",
                    }}
                  >
                    <tr>
                      <td colSpan={3} style={{ background: "#f0fdfa", color: "#0f766e", padding: "14px 16px" }}>
                        <span className="fw-bold" style={{ fontSize: "0.95rem" }}>
                          Total: {datosHistoricosFiltrados.length} empresa{datosHistoricosFiltrados.length !== 1 ? "s" : ""} analizada{datosHistoricosFiltrados.length !== 1 ? "s" : ""}
                        </span>
                      </td>
                      <td colSpan={3} style={{ background: "#f0fdfa", color: "#0f766e", padding: "14px 16px", textAlign: "center" }}>
                        <span className="fw-semibold small">
                          Promedio General Cartera: {promedioHistoricoGeneral} días
                        </span>
                      </td>
                      <td style={{ background: "#f0fdfa", color: "#0f766e", padding: "14px 16px", textAlign: "right" }}>
                        <span className="fw-bold" style={{ fontSize: "1.15rem", color: "#0f766e" }}>
                          S/ {formatearMonto(datosHistoricosFiltrados.reduce((a, b) => a + b.totalMonto, 0))}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default ReporteDiasCalleCobros;
